import {
  AlertTriangle,
  ArrowLeft,
  ExternalLink,
  BookOpen,
  CheckCircle,
  KeyRound,
  Lock,
  Mail,
  Send,
  Trash2,
  UserCog,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router";
import {
  useAdminUser,
  useConfirmUser,
  useDeleteAdminUser,
  useResendConfirmation,
  useResendInvitation,
  useSendResetPassword,
  useUnlockUser,
  useUpdateAdminUser,
  useUpdateSubscription,
} from "../../api/admin";
import { useImpersonate } from "../../hooks/useImpersonate";
import { isShopifyOrderId } from "./shopify";
import { SubscriptionTransfer } from "./SubscriptionTransfer";
import {
  AdminAuditLog,
  AdminSubscription,
  AdminSubscriptionUpdateParams,
  AdminUserDetail as AdminUserDetailData,
  AdminUserUpdateParams,
} from "../../types/admin";
import { formatDate, formatDateTime } from "../../utils/dateUtils";
import { notify } from "./notify";
import {
  Badge,
  BookStatusBadge,
  Button,
  Card,
  Field,
  Loading,
  NumberField,
  PageHeader,
  PdfLink,
  SelectField,
  ShopifyOrderWarning,
  SubscriptionBadge,
} from "./ui";

export const AdminUserDetail = () => {
  const { id } = useParams();
  const userId = Number(id);
  const navigate = useNavigate();
  const { data, isLoading } = useAdminUser(userId);
  const { impersonate, loading: impersonating, currentUser } = useImpersonate();
  const deleteUser = useDeleteAdminUser();

  if (isLoading || !data) {
    return <Loading />;
  }

  const { user, subscription, book, print_orders, stats } = data;

  const remove = () =>
    deleteUser.mutate(
      user.id,
      notify("Utilisateur supprimé", () => navigate("/admin/users")),
    );

  return (
    <>
      <button
        onClick={() => navigate("/admin/users")}
        className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-4"
      >
        <ArrowLeft className="w-4 h-4" /> Retour aux utilisateurs
      </button>

      <PageHeader
        title={user.full_name || user.email}
        subtitle={`${user.email} · ID ${user.id}`}
        action={
          <div className="flex gap-2">
            <Button
              variant="danger"
              onClick={remove}
              disabled={deleteUser.isLoading || user.id === currentUser?.id}
              confirm={`Supprimer définitivement ${user.email} ainsi que son livre, ses réponses, ses anecdotes et ses commandes ?`}
            >
              <Trash2 className="w-4 h-4" /> Supprimer
            </Button>
            <Button
              variant="primary"
              onClick={() => impersonate(user.id)}
              disabled={impersonating || user.id === currentUser?.id}
            >
              <UserCog className="w-4 h-4" /> Impersonifier
            </Button>
          </div>
        }
      />

      <div className="flex flex-wrap items-center gap-2 mb-6">
        {user.is_admin && <Badge color="red">Admin</Badge>}
        <SubscriptionBadge status={subscription?.status} />
        {subscription?.product && (
          <Badge color="blue">
            {subscription.product === "print" ? "Print" : "PDF"}
          </Badge>
        )}
        {subscription?.with_audiobook && <Badge color="purple">Audio</Badge>}
        {!user.is_confirmed && <Badge color="yellow">Non confirmé</Badge>}
        {user.is_locked && <Badge color="orange">Bloqué</Badge>}
      </div>

      {data.subscriptions.length > 1 && (
        <div className="flex gap-2 rounded-md bg-red-50 border border-red-200 px-4 py-3 mb-6 text-sm text-red-800">
          <AlertTriangle className="w-5 h-5 shrink-0" />
          <p>
            <strong>
              Ce compte a {data.subscriptions.length} abonnements.
            </strong>{" "}
            Un utilisateur ne devrait en avoir qu'un : c'est souvent un second
            achat sur le même email, destiné à quelqu'un d'autre. Réaffectez
            l'abonnement en trop (ou séparez un livre) vers le bon email. L'app
            utilise l'abonnement actif le plus ancien.
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ProfileCard userId={userId} user={user} />
        {data.subscriptions.length === 0 ? (
          <Card title="Abonnement">
            <div className="p-5 text-sm text-gray-400">
              Aucun abonnement pour cet utilisateur.
            </div>
          </Card>
        ) : (
          data.subscriptions.map((sub, index) => (
            <SubscriptionCard
              key={sub.id}
              userId={userId}
              subscription={sub}
              title={
                data.subscriptions.length > 1
                  ? `Abonnement ${index + 1}/${data.subscriptions.length}${sub.id === subscription?.id ? " (utilisé par l'app)" : ""}`
                  : "Abonnement"
              }
            />
          ))
        )}
        <BookCard book={book} stats={stats} />
        <PrintOrdersCard printOrders={print_orders} />
        <AccountCard data={data} />
        <AuditLogsCard logs={data.audit_logs} />
      </div>
    </>
  );
};

// PROFILE ====================================================================
const ProfileCard = ({
  userId,
  user,
}: {
  userId: number;
  user: import("../../types/admin").AdminUserListItem;
}) => {
  const update = useUpdateAdminUser(userId);
  const resend = useResendConfirmation(userId);
  const unlock = useUnlockUser(userId);
  const resendInvitation = useResendInvitation(userId);
  const resetPassword = useSendResetPassword(userId);
  const confirmUser = useConfirmUser(userId);
  const [form, setForm] = useState<AdminUserUpdateParams>({});

  useEffect(() => {
    setForm({
      first_name: user.first_name ?? "",
      last_name: user.last_name ?? "",
      email: user.email,
      is_admin: user.is_admin,
    });
  }, [user]);

  const save = () =>
    update.mutate(form, {
      onSuccess: () => {
        toast.success("Profil mis à jour");
      },
    });

  return (
    <Card title="Profil">
      <div className="p-5 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Field
            label="Prénom"
            value={form.first_name ?? ""}
            onChange={(v) => setForm((f) => ({ ...f, first_name: v }))}
          />
          <Field
            label="Nom"
            value={form.last_name ?? ""}
            onChange={(v) => setForm((f) => ({ ...f, last_name: v }))}
          />
        </div>
        <Field
          label="Email"
          value={form.email ?? ""}
          onChange={(v) => setForm((f) => ({ ...f, email: v }))}
        />
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.is_admin ?? false}
            onChange={(e) =>
              setForm((f) => ({ ...f, is_admin: e.target.checked }))
            }
            className="rounded border-gray-300"
          />
          Administrateur
        </label>

        <div className="flex flex-wrap gap-2 pt-2">
          <button
            onClick={save}
            disabled={update.isLoading}
            className="px-3 py-1.5 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50"
          >
            Enregistrer
          </button>
          {!user.is_confirmed && (
            <>
              <Button
                onClick={() =>
                  resend.mutate(
                    undefined,
                    notify("Email de confirmation envoyé"),
                  )
                }
                disabled={resend.isLoading}
              >
                <Mail className="w-4 h-4" /> Renvoyer la confirmation
              </Button>
              <Button
                onClick={() =>
                  confirmUser.mutate(undefined, notify("Compte confirmé"))
                }
                disabled={confirmUser.isLoading}
              >
                <CheckCircle className="w-4 h-4" /> Confirmer
              </Button>
            </>
          )}
          {user.is_locked && (
            <Button
              onClick={() =>
                unlock.mutate(undefined, notify("Compte débloqué"))
              }
              disabled={unlock.isLoading}
            >
              <Lock className="w-4 h-4" /> Débloquer
            </Button>
          )}
          <Button
            onClick={() =>
              resendInvitation.mutate(undefined, notify("Invitation renvoyée"))
            }
            disabled={resendInvitation.isLoading}
          >
            <Send className="w-4 h-4" /> Renvoyer l'invitation
          </Button>
          <Button
            onClick={() =>
              resetPassword.mutate(
                undefined,
                notify("Email de réinitialisation envoyé"),
              )
            }
            disabled={resetPassword.isLoading}
          >
            <KeyRound className="w-4 h-4" /> Réinitialiser le mot de passe
          </Button>
        </div>

        <div className="pt-2 text-xs text-gray-400 space-y-0.5">
          <p>Connexions : {user.sign_in_count}</p>
          <p>Dernière connexion : {formatDate(user.last_sign_in_at)}</p>
          <p>Langue : {user.language ?? "—"}</p>
        </div>
      </div>
    </Card>
  );
};

// SUBSCRIPTION ===============================================================
const SubscriptionCard = ({
  userId,
  subscription,
  title,
}: {
  userId: number;
  subscription: AdminSubscription;
  title: string;
}) => {
  const update = useUpdateSubscription(userId);
  const [form, setForm] = useState<AdminSubscriptionUpdateParams>({});

  useEffect(() => {
    setForm({
      status: subscription.status,
      product: subscription.product,
      with_audiobook: subscription.with_audiobook,
      books_purchased: subscription.books_purchased,
      books_remaining: subscription.books_remaining,
      books_printed: subscription.books_printed,
      shopify_order_id: subscription.shopify_order_id ?? "",
      shopify_order_name: subscription.shopify_order_name ?? "",
    });
  }, [subscription]);

  const save = () =>
    update.mutate(
      { id: subscription.id, params: form },
      notify("Abonnement mis à jour"),
    );

  return (
    <Card title={title}>
      <div className="p-5 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label="Statut"
            value={form.status ?? ""}
            onChange={(v) => setForm((f) => ({ ...f, status: v }))}
            options={[
              ["pending", "En attente"],
              ["active", "Actif"],
              ["cancelled", "Annulé"],
            ]}
          />
          <SelectField
            label="Produit"
            value={form.product ?? ""}
            onChange={(v) => setForm((f) => ({ ...f, product: v }))}
            options={[
              ["pdf", "PDF"],
              ["print", "Print"],
            ]}
          />
        </div>
        <div className="grid grid-cols-3 gap-3">
          <NumberField
            label="Achetés"
            value={form.books_purchased}
            onChange={(v) => setForm((f) => ({ ...f, books_purchased: v }))}
          />
          <NumberField
            label="Restants"
            value={form.books_remaining}
            onChange={(v) => setForm((f) => ({ ...f, books_remaining: v }))}
          />
          <NumberField
            label="Imprimés"
            value={form.books_printed}
            onChange={(v) => setForm((f) => ({ ...f, books_printed: v }))}
          />
        </div>
        <label className="flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={form.with_audiobook ?? false}
            onChange={(e) =>
              setForm((f) => ({ ...f, with_audiobook: e.target.checked }))
            }
            className="rounded border-gray-300"
          />
          Audiobook inclus
        </label>

        <div className="grid grid-cols-3 gap-3">
          <div className="col-span-2">
            <Field
              label="Commande Shopify (lien de la commande ou son ID)"
              value={form.shopify_order_id ?? ""}
              onChange={(v) => setForm((f) => ({ ...f, shopify_order_id: v }))}
            />
          </div>
          <Field
            label="N° de commande"
            value={form.shopify_order_name ?? ""}
            onChange={(v) => setForm((f) => ({ ...f, shopify_order_name: v }))}
          />
        </div>
        {subscription.shopify_admin_url ? (
          <a
            href={subscription.shopify_admin_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:underline"
          >
            <ExternalLink className="w-3.5 h-3.5" /> Voir la commande{" "}
            {subscription.shopify_order_name ?? subscription.shopify_order_id}{" "}
            dans Shopify
          </a>
        ) : isShopifyOrderId(subscription.shopify_order_id) ? (
          <p className="text-xs text-gray-400">
            Commande Shopify {subscription.shopify_order_id} (lien indisponible
            : SHOPIFY_SHOP_DOMAIN n'est pas configuré)
          </p>
        ) : (
          <ShopifyOrderWarning />
        )}
        {subscription.is_gift && (
          <p className="text-xs text-gray-500">
            Cadeau de {subscription.offered_by_full_name} (
            {subscription.offered_by_email})
          </p>
        )}
        <p className="text-xs text-gray-400">
          Acheté le {formatDate(subscription.purchased_at)} · créé le{" "}
          {formatDate(subscription.created_at)}
        </p>

        <Button variant="primary" onClick={save} disabled={update.isLoading}>
          Enregistrer
        </Button>

        <div className="pt-3 border-t border-gray-100">
          <SubscriptionTransfer subscription={subscription} />
        </div>
      </div>
    </Card>
  );
};

// BOOK =======================================================================
const BookCard = ({
  book,
  stats,
}: {
  book: AdminUserDetailData["book"];
  stats: AdminUserDetailData["stats"];
}) => {
  const navigate = useNavigate();

  return (
    <Card
      title="Livre"
      action={
        book && (
          <Button size="sm" onClick={() => navigate(`/admin/books/${book.id}`)}>
            <BookOpen className="w-3.5 h-3.5" /> Tout voir et débloquer
          </Button>
        )
      }
    >
      <div className="p-5 space-y-3">
        {book ? (
          <>
            <div className="flex items-center justify-between">
              <span className="font-medium text-gray-900">
                {book.title || "Sans titre"}
              </span>
              <BookStatusBadge status={book.status} />
            </div>

            <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
              <span>
                Réponses : {stats.answers_completed}/{stats.questions_total}
              </span>
              <span>Chapitres générés : {stats.generated_chapters}</span>
              <span>Anecdotes : {stats.anecdotes}</span>
              <span>Pages : {book.total_page_count ?? "—"}</span>
            </div>

            <div className="flex flex-wrap gap-2">
              {book.content_pdf_url && (
                <PdfLink url={book.content_pdf_url} label="PDF contenu" />
              )}
              {book.cover_pdf_url && (
                <PdfLink url={book.cover_pdf_url} label="PDF couverture" />
              )}
              {book.numeric_pdf_url && (
                <PdfLink url={book.numeric_pdf_url} label="PDF numérique" />
              )}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-400">
            Aucun livre pour cet utilisateur.
          </p>
        )}
      </div>
    </Card>
  );
};

// ACCOUNT ====================================================================
const AccountCard = ({ data }: { data: AdminUserDetailData }) => (
  <Card title="Compte">
    <dl className="divide-y divide-gray-100 text-sm">
      {[
        ["Inscrit le", formatDateTime(data.user.created_at)],
        ["Invitation programmée", formatDateTime(data.invitation.scheduled_at)],
        ["Invitation envoyée", formatDateTime(data.invitation.sent_at)],
        ["Invitation acceptée", formatDateTime(data.invitation.accepted_at)],
        ["Connexion en cours depuis", formatDateTime(data.sign_in.current_at)],
        ["IP actuelle", data.sign_in.current_ip ?? "—"],
        ["IP précédente", data.sign_in.last_ip ?? "—"],
        ["Échecs de connexion", data.sign_in.failed_attempts],
      ].map(([label, value]) => (
        <div key={label} className="flex justify-between px-5 py-2">
          <dt className="text-gray-500">{label}</dt>
          <dd className="text-gray-900">{value}</dd>
        </div>
      ))}
    </dl>
  </Card>
);

// AUDIT ======================================================================
const AuditLogsCard = ({ logs }: { logs: AdminAuditLog[] }) => (
  <Card title="Historique admin">
    {logs.length === 0 ? (
      <div className="p-5 text-sm text-gray-400">
        Aucune action admin sur ce compte.
      </div>
    ) : (
      <ul className="divide-y divide-gray-100">
        {logs.map((log) => (
          <li key={log.id} className="px-5 py-2.5 text-xs">
            <div className="flex justify-between">
              <span className="font-medium text-gray-800">{log.action}</span>
              <span className="text-gray-400">
                {formatDateTime(log.created_at)}
              </span>
            </div>
            <p className="text-gray-500">{log.admin ?? "admin supprimé"}</p>
          </li>
        ))}
      </ul>
    )}
  </Card>
);

// PRINT ORDERS ===============================================================
const PrintOrdersCard = ({
  printOrders,
}: {
  printOrders: import("../../types/admin").AdminPrintOrder[];
}) => (
  <Card title={`Commandes d'impression (${printOrders.length})`}>
    {printOrders.length === 0 ? (
      <div className="p-5 text-sm text-gray-400">Aucune commande.</div>
    ) : (
      <ul className="divide-y divide-gray-100">
        {printOrders.map((order) => (
          <li key={order.id} className="px-5 py-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-900">
                {order.quantity ?? 0} exemplaire(s)
              </span>
              {order.is_confirmed ? (
                <Badge color="green">Confirmée</Badge>
              ) : (
                <Badge color="yellow">En attente</Badge>
              )}
            </div>
            <p className="text-xs text-gray-500 whitespace-pre-line mt-1">
              {order.delivery_address}
            </p>
            <p className="text-xs text-gray-400 mt-1">
              {order.phone} · créée le {formatDate(order.created_at)}
            </p>
          </li>
        ))}
      </ul>
    )}
  </Card>
);
